import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import { formatINR } from "@/lib/format";
import { Link } from "react-router-dom";

// Fix default marker icon URLs (CDN)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const makePriceIcon = (text, type) =>
  L.divIcon({
    className: "",
    html: `<div class="price-pin ${type === "rent" ? "rent" : ""}">${text}</div>`,
    iconSize: [80, 28],
    iconAnchor: [40, 28],
  });

const Recenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const POI_STYLES = {
  schools: { color: "#1E4338", label: "School" },
  hospitals: { color: "#C85A32", label: "Hospital" },
  transit: { color: "#8a6420", label: "Transit" },
};

export const LeafletMap = ({ properties = [], neighborhoods = [], pois = null, center = [12.3168, 76.6412], zoom = 13, height = "100%" }) => {
  const mapRef = useRef(null);
  return (
    <div style={{ height, width: "100%" }} className="rounded-2xl overflow-hidden border border-[var(--line)]" data-testid="leaflet-map">
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} ref={mapRef} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {neighborhoods.map((n) => (
          <Circle
            key={n.name}
            center={[n.lat, n.lng]}
            radius={700}
            pathOptions={{ color: "#1E4338", fillColor: "#1E4338", fillOpacity: 0.06, weight: 1.5, dashArray: "4 4" }}
          >
            <Popup>
              <div className="min-w-[200px]">
                <div className="font-serif text-base font-semibold">{n.name}</div>
                <div className="text-xs text-gray-500">{n.tagline}</div>
                <div className="text-xs mt-1">₹{n.avg_sqft_min}–₹{n.avg_sqft_max}/sq.ft</div>
                <div className="text-xs mt-1">{n.property_count ?? 0} listings</div>
              </div>
            </Popup>
          </Circle>
        ))}
        {pois && Object.entries(pois).map(([kind, list]) =>
          list.map((poi, idx) => (
            <CircleMarker
              key={`${kind}-${idx}`}
              center={[poi.lat, poi.lng]}
              radius={7}
              pathOptions={{
                color: POI_STYLES[kind]?.color || "#555",
                fillColor: POI_STYLES[kind]?.color || "#555",
                fillOpacity: 0.9,
                weight: 2,
              }}
            >
              <Popup>
                <div className="min-w-[160px]">
                  <div className="font-semibold text-sm">{poi.name}</div>
                  <div className="text-xs text-gray-500 capitalize">{POI_STYLES[kind]?.label || kind}</div>
                </div>
              </Popup>
            </CircleMarker>
          ))
        )}
        {properties.map((p) => (
          <Marker
            key={p.id}
            position={[p.lat, p.lng]}
            icon={makePriceIcon(formatINR(p.price, p.listing_type), p.listing_type)}
          >
            <Popup>
              <div className="min-w-[220px]">
                <div className="font-serif text-base font-semibold mb-1">{p.title}</div>
                <div className="text-xs text-gray-500 mb-2">{p.locality}, Mysuru · {p.bhk > 0 ? `${p.bhk} BHK · ` : ""}{p.area_sqft} sq.ft</div>
                <Link to={`/property/${p.id}`} className="text-xs font-semibold text-[var(--primary)]" data-testid={`map-popup-link-${p.id}`}>View details →</Link>
              </div>
            </Popup>
          </Marker>
        ))}
        <Recenter center={center} />
      </MapContainer>
    </div>
  );
};
